import { Role, User } from '@/types';

// Hierarchy: SuperAdmin is highest (0), OrgAdmin is middle (1), DataOwner is lower (2)
export const ROLE_HIERARCHY: Role[] = ['SuperAdmin', 'OrgAdmin', 'DataOwner'];

export const ROLE_LABELS: Record<Role, string> = {
  SuperAdmin: 'Super Admin',
  OrgAdmin: 'Org Admin',
  DataOwner: 'Data Owner',
};

/**
 * Gets the permission level for a role. Lower number = higher permission.
 */
const getRoleLevel = (role: Role): number => ROLE_HIERARCHY.indexOf(role);

/**
 * Checks if the user's current role matches the required role.
 */
export const hasRole = (currentRole: Role | undefined, requiredRole: Role): boolean => currentRole === requiredRole;

/**
 * Checks if the user's current role is one of the required roles.
 */
export const hasAnyRole = (currentRole: Role | undefined, requiredRoles: Role[]): boolean =>
  !!currentRole && requiredRoles.includes(currentRole);

/**
 * Checks if the user's current role has at least the permission level of the required role.
 * Since index 0 is highest, we check if currentRole index <= requiredRole index.
 */
export const hasMinimumRole = (currentRole: Role | undefined, requiredRole: Role): boolean => {
  if (!currentRole) return false;
  return getRoleLevel(currentRole) <= getRoleLevel(requiredRole);
};

/**
 * Checks if the current role is SuperAdmin.
 */
export const isSuperAdmin = (currentRole: Role | undefined): boolean => currentRole === 'SuperAdmin';

/**
 * Checks if the current role is OrgAdmin.
 */
export const isOrgAdmin = (currentRole: Role | undefined): boolean => currentRole === 'OrgAdmin';

/**
 * Checks if the current role is DataOwner.
 */
export const isDataOwner = (currentRole: Role | undefined): boolean => currentRole === 'DataOwner';

/**
 * Gets a human-readable label for a role.
 */
export const getRoleLabel = (role: Role | undefined): string | null => (role ? (ROLE_LABELS[role] ?? role) : null);

/**
 * Checks if the user has platform-wide super admin privileges.
 * This is independent of org-specific roles and checks the user.is_super_admin field.
 */
export const isGlobalSuperAdmin = (user: User | null | undefined): boolean => user?.is_super_admin ?? false;

/**
 * Gets all available roles.
 */
export const getAllRoles = (): ReadonlyArray<Role> => ROLE_HIERARCHY;
